<template>
  <div class="formulaPanel" :class="{ indent: !noIndent }">
    <div :class="{ active: collapsePanel }" class="expandPanel" @click="toggleCollapse">
      {{ label || parseTranslation(ALTERNATIVE_FORMULA) }}
      <span>
        <Arrow />
      </span>
    </div>

    <div class="collapsePanel" :class="{ active: collapsePanel }">
      <div
        class="single-condition"
        :class="{ indent: !noIndent }"
        v-for="(condition, index) in conditions"
        :key="index"
      >
        <select
          class="logical-select"
          v-if="index !== FIRST_ITEM_INDEX"
          :value="condition.logicalOperation || ''"
          @change="event => updateOperator(event, index)"
        >
          <option
            v-for="operator in conditionalOperators"
            :key="operator"
            :value="operator"
          >
            {{ operator }}
          </option>
        </select>

        <select class="formula-type-select" :v-model="condition.type">
          <option value="" disabled>{{ parseTranslation(FORMULA_TYPE) }}</option>

          <option
            v-for="[formulaType, displayText] in formulaTypes"
            :key="formulaType"
            :value="formulaType"
          >
            {{ displayText }}
          </option>
        </select>

        <component
          :is="getFormulaTypeComponent(condition.type)"
          :id="`${id}-formula-${index}`"
          :available_questions="availableQuestions"
          :condition="condition"
          :update_condition="updated => updateSpecificCondition(updated, index)"
          :remove_self="() => removeCondition(index)"
        />

        <OperatorRenderer
          :id="`${id}-operator-${index}`"
          :available_questions="availableQuestions"
          :condition="condition"
          :update_condition="updated => updateSpecificCondition(updated, index)"
          :remove_self="() => removeCondition(index)"
        />

        <button
          v-if="allowFirstRemoval ? true : index !== FIRST_ITEM_INDEX"
          class="remove-btn"
          @click="() => removeCondition(index)"
        >
          <Close />
        </button>
      </div>

      <button class="add-condition-btn" @click="addCondition">
        <span class="plus-sign">+</span>
        <span>{{ parseTranslation(ADD_CONDITION) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import Arrow from '@/components/imgs/ArrowExpandGroup'
import Close from '@/components/imgs/Close'

import AlternativesType from './AlternativesType.vue'
import OperatorRenderer from './OperatorRenderer.vue'

import {
  questionsConfig,
  FORMULA_TYPES,
  CONDITIONAL_OPERATORS,
  formulaTypeToInitialConditionGetter,
} from '@/utils/questions'

import * as translationFormulaRenderer from './FormulaRenderer.translations'

const { default: translations, ...translationKeys } = translationFormulaRenderer

export default {
  name: 'FormulaRenderer',

  components: {
    AlternativesType,
    OperatorRenderer,
    Arrow,
    Close,
  },

  data() {
    return {
      translations,
      ...translationKeys,
      collapsePanel: false,
      conditionalOperators: Object.values(CONDITIONAL_OPERATORS),
      FIRST_ITEM_INDEX: 0,
    }
  },

  props: {
    updateAlternative: {
      required: true,
      type: Function,
    },
    questions_groups_index: {
      required: true,
      type: Number,
    },
    question_index: {
      required: true,
      type: Number,
    },
    id: {
      required: true,
      type: String,
    },
    alternative: {
      required: true,
      type: Object,
    },

    label: {
      required: false,
      type: String,
    },

    noIndent: {
      required: false,
      type: Boolean,
    },

    allowFirstRemoval: {
      required: false,
      type: Boolean,
    },

    conditionsProperty: {
      required: false,
      type: String,
      default: 'conditions',
    },
  },

  computed: {
    ...mapGetters(['darkmode']),

    conditions() {
      return this.alternative?.[this.conditionsProperty] || []
    },

    availableQuestions() {
      const groups = this.$store.state.questionGroups || []

      const priorQuestions = groups
        .filter((_, index) => index < this.questions_groups_index)
        .map(({ questions }) => questions)
        .flat()
        .filter(question => this.validateIfQuestionHasFormula(question))

      const currentGroupQuestions = groups[this.questions_groups_index].questions || []

      const questionsBeforeCurrent = currentGroupQuestions.filter((question, index) => {
        if (index >= this.question_index) return false

        return this.validateIfQuestionHasFormula(question)
      })

      return [...priorQuestions, ...questionsBeforeCurrent]
    },

    formulaTypes() {
      const options = Object.entries(FORMULA_TYPES)

      const filteredByAvailability = options.filter(([formulaType]) => {
        const availableQuestionForThisType = this.availableQuestions.some(
          ({ type }) => questionsConfig[type].formulaReferences?.[formulaType].length,
        )

        return availableQuestionForThisType
      })

      return filteredByAvailability
    },
  },

  methods: {
    ...mapActions(['sendMessage']),

    validateIfQuestionHasFormula(question) {
      const { type, answers, subQuestions } = question

      const { formulaReferences, hasSubQuestion, hasAnswer } = questionsConfig[type]

      if (!formulaReferences) return false

      const answersOk = hasAnswer ? !!answers.length : true
      const subQuestionsOk = hasSubQuestion ? !!subQuestions.length : true

      return answersOk && subQuestionsOk
    },

    toggleCollapse() {
      this.collapsePanel = !this.collapsePanel
    },

    updateAlternativeConditions(updatedConditions) {
      const updatedAlternative = {
        ...this.alternative,
        [this.conditionsProperty]: updatedConditions,
      }

      this.updateAlternative(updatedAlternative)
    },

    getNewCondition() {
      const [[formulaType]] = this.formulaTypes

      const conditionalGetter = formulaTypeToInitialConditionGetter[formulaType]

      const newCondition = conditionalGetter(this.availableQuestions)

      if (this.conditions.length) {
        const [logicalOperation] = this.conditionalOperators

        newCondition.logicalOperation = logicalOperation
      }

      return newCondition
    },

    addCondition() {
      if (!this.formulaTypes.length)
        return this.sendMessage(
          this.translations[this.language][this.ADD_CONDITION_MESSAGE],
        )

      const conditions = [...this.conditions, this.getNewCondition()]

      this.updateAlternativeConditions(conditions)
    },

    removeCondition(conditionIndex) {
      const updated = this.conditions.filter((_, index) => index !== conditionIndex)

      if (!updated.length && this.allowFirstRemoval)
        return this.updateAlternativeConditions(null)

      const SINGLE_CONDITION = 1

      if (updated.length !== SINGLE_CONDITION)
        return this.updateAlternativeConditions(updated)

      const [{ logicalOperation: _, ...condition }] = updated

      this.updateAlternativeConditions([condition])
    },

    updateSpecificCondition(updated, updateIndex) {
      const conditions = this.conditions.map((condition, conditionIndex) =>
        updateIndex === conditionIndex ? updated : condition,
      )

      this.updateAlternativeConditions(conditions)
    },

    updateOperator({ target: { value } }, updateIndex) {
      const updatedConditions = this.conditions.map((condition, index) => {
        if (index !== updateIndex) return condition

        return {
          ...condition,
          logicalOperation: value,
        }
      })

      this.updateAlternativeConditions(updatedConditions)
    },

    getFormulaTypeComponent(type) {
      switch (type) {
        case FORMULA_TYPES.alternatives:
          return 'AlternativesType'
        default:
          throw new Error(this.translations[this.language][this.DEFAULT_SWITCH])
      }
    },
  },
}
</script>

<style scoped lang="scss">
.indent {
  padding: 10px 40px;
}

.expandPanel {
  @include flexAlignCenter;
  cursor: pointer;
  white-space: nowrap;
  @include tipography(16);
  font-weight: bold;
  span {
    @include flexAlignCenter;
    @include boxSizing(15);

    margin-left: 10px;
    transition: all 0.3s ease-in;
    transform: rotate(180deg);
  }

  &.expandAll {
    span {
      transform: rotate(0);
    }
    display: block;
  }

  &.active {
    span {
      transform: rotate(0);
    }
  }

  .light & {
    color: $gray-6;
  }
}
.collapsePanel {
  display: none;
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;
  margin-top: 10px;

  &.active,
  &.expandAll {
    display: block;
  }
}

.remove-btn {
  @include transparentButton;

  svg {
    @include boxSizing(15);
  }

  display: none;
  margin-left: 12px;
}

.single-condition {
  @include flexAlignCenter;
  padding: 10px 0;

  &.indent {
    padding: 10px 40px;
  }

  &:last-of-type {
    padding-bottom: 25px;
  }

  &:hover .remove-btn {
    display: flex;
  }
}

.formula-type-select {
  &,
  option {
    text-transform: capitalize;
  }
}

.add-condition-btn {
  @include transparentButton;
  @include tipography(16);
  color: $blue;
  margin: 0 0 15px 50px;

  .plus-sign {
    @include flexMiddle;
    @include boxSizing(20);

    color: $white;
    margin-right: 12px;
    background: $blue;
    font-size: 25px;
    border-radius: 50%;
  }
}

select {
  @include baseSelect;

  option {
    color: $gray-2;
  }
}

.logical-select {
  margin-left: 10px;
  margin-right: 6px;
}

@media (max-width: 575.98px) {
  .single-condition {
    flex-direction: column;
  }

  .single-condition > * {
    margin-bottom: 15px;
  }
}
</style>
